import { EventEmitter, Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import type { WorkSheet } from 'xlsx';

type XlsxModule = typeof import('xlsx');


@Injectable()
export class ExportToExcelService implements OnDestroy {
  exportClick: EventEmitter<void> = new EventEmitter<void>();

  private userReportsData: any[] = [];
  private userAlarmsData: any[] = [];
  private isDataReady: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);


  constructor(private translate: TranslateService,) {}

  ngOnDestroy(): void {
    this.exportClick.complete();
  }

  setUserReportsData(data: any[]) {
    this.userReportsData = data;
    this.checkIfDataIsReady();
  }

  setUserAlarmsData(data: any[]) {
    this.userAlarmsData = data;
    this.checkIfDataIsReady();
  }

  private checkIfDataIsReady() {
    // If both user reports and alarms data are available, set the flag to true
   if (this.userReportsData.length > 0 || this.userAlarmsData.length > 0) {
      this.isDataReady.next(true);
    } else {
      this.isDataReady.next(false);
    }
  }

  async exportToExcel(exportToExcelFileName: string): Promise<void> {
    if (!this.isDataReady.value) {
      console.log('Data is not ready for export.');
      return;
    }

    const dataSets = [
      { sheetName: 'User Reports', data: this.userReportsData },
      { sheetName: 'User Alarms', data: this.userAlarmsData },
    ];

    await this.exportMultipleArraysToExcel(dataSets, exportToExcelFileName);
  }

  async exportMultipleArraysToExcel(dataSets: { sheetName: string, data: any[] }[], exportToExcelFileName : string) {
      try {

        const XLSX: XlsxModule = await import('xlsx');

        const wb = XLSX.utils.book_new();
    
        dataSets.forEach(({ sheetName, data }) => {
          const columns = sheetName === 'User Reports'
            ? this._userReportColumns
            : this._userAlarmColumns;
    
          // Each header now includes both `key` and `colHeader`
          const headers = columns.map(col => ({ key: col.key, colHeader: col.colHeader }));
          const headerKeys = headers.map(h => h.key);

          let ws: WorkSheet;

          const isEmpty = val =>
            val === null ||
            val === undefined ||
            val === "" ||
            (typeof val === "string" && val.trim() === "");

            const isArrayCompletelyEmpty = (arr: any[]) =>
                                      !arr || arr.length === 0 || arr.every(obj =>
                                        Object.values(obj).every(isEmpty)
                                      );

          if (!data || data.length === 0 || isArrayCompletelyEmpty(data)) {
            ws = this.createHeaderOnlySheet(XLSX, headerKeys);
          } else {
            const dataRows = this.prepareDataForSheet(data, headers);
            ws = XLSX.utils.json_to_sheet(dataRows, { header: headerKeys });
          }
    
          this.translateSheetHeaders(XLSX, ws, headers, sheetName);
    
          XLSX.utils.book_append_sheet(wb, ws, sheetName);
        });
    
        XLSX.writeFile(wb, `${exportToExcelFileName}.xlsx`);
      } catch (error) {
        console.error('Error exporting multiple sheets:', error);
      }
    }
     
    private prepareDataForSheet(
      data: any[],
      headers: { key: string, colHeader: string }[]
    ): any[] {
      return data.map(item => {
        const filteredItem: Record<string, any> = {};
    
        headers.forEach(({ key }) => {
          if (key === 'Alerts') {
            filteredItem[key] = 'True'; // only when actual data
          } else if (key === 'User Scope') {
            filteredItem[key] = item['isUserInGroup'] ? 'Group' : 'User';
          } else {
            filteredItem[key] = item[key] === '-' ? 'N/A' : item[key];
          }
        });
    
        return filteredItem;
      });
    }
    
    private createDummyRow(keys: string[]): Record<string, string> {
      const dummy: Record<string, string> = {};
      keys.forEach(key => dummy[key] = ''); // No static values
      return dummy;
    }
    
    private createHeaderOnlySheet(XLSX: XlsxModule, keys: string[]): WorkSheet {
      const headerRow = keys.reduce((acc, key) => {
        acc[key] = '';
        return acc;
      }, {} as Record<string, string>);
    
      const sheet = XLSX.utils.json_to_sheet([headerRow], { header: keys });
    
      // Remove all values, keep only header row
      Object.keys(sheet).forEach(cell => {
        if (!cell.endsWith('1') && cell !== '!ref') {
          delete sheet[cell];
        }
      });
    
      sheet['!ref'] = 'A1:' + XLSX.utils.encode_col(keys.length - 1) + '1';
      return sheet;
    }
    
    
    private translateSheetHeaders(
      XLSX: XlsxModule,
      ws: WorkSheet,
      headers: { key: string, colHeader: string }[],
      sheetName: string
    ) {
      if (!ws['!ref']) return;
    
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_col(C) + '1';
        if (ws[cellAddress] && typeof ws[cellAddress].v === 'string') {
          const headerKey = ws[cellAddress].v;
          const headerDef = headers.find(h => h.key === headerKey);
          const displayName = headerDef?.colHeader || headerKey;
          ws[cellAddress].v = this.translateHeader(displayName, sheetName);
        }
      }
    }
    
    
      _userReportColumns = [
        { key: 'User Scope', type: 'string', colHeader : 'User Scope' },
        { key: 'organization', type: 'string', colHeader : 'Organization' },
        { key: 'reportType', type: 'string', colHeader : 'ReportType' },
        { key: 'reportName', type: 'string', colHeader : 'ReportName' },
        { key: 'Alerts', type: 'string', colHeader : 'Alerts' },
        { key: 'userName', type: 'string', colHeader : 'UserName' },
        { key: 'firstName', type: 'string', colHeader : 'FirstName' },
        { key: 'lastName', type: 'string', colHeader : 'LastName' }
      ];
    
  
      _userAlarmColumns = [
        { key: 'User Scope', type: 'string', colHeader : 'User Scope' },
        { key: 'organizationName', type: 'string', colHeader : 'Organization' },
        { key: 'locationName', type: 'string', colHeader : 'Location' },
        { key: 'tankName', type: 'string', colHeader : 'Tank' },
        { key: 'rtuNumber', type: 'string', colHeader : 'RTU' },
        { key: 'entityType', type: 'string', colHeader : 'AlertType'},
        { key: 'Alerts', type: 'string', colHeader : 'Alerts'},
        { key: 'userName', type: 'string', colHeader : 'UserName' },
        { key: 'firstName', type: 'string', colHeader : 'FirstName' },
        { key: 'lastName', type: 'string', colHeader : 'LastName' }
      ];
      
      translateHeader(header: string, sheetname : string): string {
        if (typeof header !== 'string' || header === 'User Scope') {
          return header;
        }
        let baseKey = sheetname === 'User Reports'
                      ? 'User.UserReportsGridColumn.'
                      : 'User.UserAlarmsGridColumn.';
        let columnName = header.charAt(0).toUpperCase() + header.slice(1);
        return this.translate.instant(baseKey + columnName);
      }
}
